import React, { useEffect, useState } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'
import {
  GOOGLE_OAUTH_REDIRECT_URI,
  IS_LOCAL,
  RADOM_COLORS,
  RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
  RADOM_TOKEN_LOCAL_STORAGE_KEY,
  WINDOW_SIZE
} from '../util/Constants'
import { observer } from 'mobx-react'
import User from '../state/User'
import RadomLogo from '../icons/Radom'
import GoogleLogo from '../images/Google.svg'
import Metamask from '../images/Metamask.svg'
import Brave from '../images/Brave.png'
import styled, { keyframes } from 'styled-components'
import Radom from '../state/Radom'
import { useGoogleLogin } from '@react-oauth/google'
import EmailLogin from './EmailLogin'
import { TextInput } from './Input'
import { errorToast, successToast } from '../util/Util'

const LoginVideo2 = new URL('../images/login_vid2.mp4', import.meta.url).toString()

export enum LoginStep {
  Login,
  StartSignUp,
  VerifySignUp,
  CompleteSignUp,
  ForgotPassword,
  PasswordResetEmailSent,
  PasswordResetCodeVerified,
}

const VidContainer = styled.div`
  display: block;
  overflow: hidden;
  flex: 0 0 clamp(340px, 34vw, 500px);
  width: clamp(340px, 34vw, 500px);
  height: 100%;
  min-height: 0;
  background: var(--surface, #ffffff);
  border-left: 1px solid var(--surface-pressed, #edf1f7);

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    display: none;
  }
`

const ScaleInSlow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Vid = styled.video`
  display: block;
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center center;
  animation: ${ScaleInSlow} 3s ease;
`

const Container = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  height: 100dvh;
  min-height: 100dvh;
  overflow: hidden;
  background: var(--surface, #ffffff);
`

const LoginContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 0;
  z-index: 1;
  overflow: hidden;
  background: var(--surface, #ffffff);
  
  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    width: 100%;
    overflow: auto;
  }
`

const LoginShell = styled.div`
  width: min(960px, 100%);
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 26px 38px 24px;
  overflow: hidden;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    height: auto;
    min-height: 100%;
    padding: 18px 18px 24px;
    overflow: visible;
  }
`

const LoginTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const LoginTopBarAction = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 148px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    min-width: 0;
  }
`

const LoginStage = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  padding: clamp(44px, 9vh, 92px) 0 0;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    justify-content: flex-start;
    overflow: visible;
    padding-top: 24px;
  }
`

const LoginContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: min(352px, 100%);
  max-width: 100%;
`

const LoginIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  text-align: center;
  min-height: 74px;
  justify-content: flex-start;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    min-height: auto;
  }
`

const LoginTitle = styled.h1`
  font-size: 25px;
  font-weight: 470;
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1.12;
  color: ${RADOM_COLORS.NEW_GRAY_1};
`

const LoginSubtitle = styled.span`
  font-size: 13px;
  color: var(--text-muted, #6f7888);
  line-height: 1.5;
  max-width: 304px;
`

const LoginCard = styled.div<{ $compact?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
  ${({ $compact }) => !$compact && 'min-height: 292px;'}

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    min-height: auto;
  }
`

const PrimaryAuthSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 136px;
  width: 100%;
`

const PrimaryAuthSlotSpacer = styled.div`
  min-height: 136px;
  width: 100%;
`

const ProviderButton = styled(SecondaryButton)`
  background-color: var(--surface, #ffffff) !important;
  border: 1px solid var(--border-strong, #d8dde7);
  border-radius: 12px;
  width: 100%;
  color: ${RADOM_COLORS.BLACK};
  gap: 10px;
  min-height: 42px;
  padding: 0 14px !important;
  font-size: 13.75px;
  font-weight: 465;
  letter-spacing: -0.01em;

  &:hover {
    background-color: var(--surface-hover, #f8f9fb) !important;
    border-color: var(--border-strong, #c7d0dd);
    box-shadow: none;
  }
  
  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(41, 99, 255, 0.08);
  }
`

const SocialAuthStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const DividerLine = styled.div`
  height: 1px;
  flex: 1;
  background: #e4e8f0;
`

const DividerLabel = styled.span`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 10.5px;
  font-weight: 480;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const CardFooterNote = styled.div`
  font-size: 12px;
  text-align: center;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  line-height: 1.5;
  max-width: 320px;
  align-self: center;
`

const FooterLink = styled.a`
  color: ${RADOM_COLORS.NEW_BLUE};
  text-decoration: underline;
  text-underline-offset: 2px;
`

const WithConnectedWallet = observer(props => {
  const [loginStep, setLoginStep] = useState(LoginStep.Login)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  useEffect(() => {
    localStorage.removeItem('isWalletLogin')
  }, [])

  useEffect(() => {
    setIsGoogleLoading(false)
  }, [loginStep])

  const isPrimaryAuthStep = loginStep === LoginStep.Login || loginStep === LoginStep.StartSignUp
  const topBarActionLabel = loginStep === LoginStep.Login ? 'Create an account' : 'Back to sign in'
  const topBarActionStep = loginStep === LoginStep.Login ? LoginStep.StartSignUp : LoginStep.Login
  const authCardMinHeight = isPrimaryAuthStep
    ? (loginStep === LoginStep.Login ? 386 : 372)
    : (loginStep === LoginStep.CompleteSignUp || loginStep === LoginStep.PasswordResetCodeVerified ? 344 : 274)
  const footerAgreementCopy = loginStep === LoginStep.Login
    ? 'By continuing'
    : loginStep === LoginStep.StartSignUp
      ? 'By creating an account'
      : undefined
  const googleButtonLabel = loginStep === LoginStep.Login ? 'Sign in with Google' : 'Continue with Google'
  const showWalletLogin = loginStep === LoginStep.Login

  const loginIntro = (() => {
    switch (loginStep) {
    case LoginStep.StartSignUp:
      return {
        title: 'Create a Radom account',
        subtitle: 'Create your account to get started.'
      }
    case LoginStep.VerifySignUp:
      return {
        title: 'Check your email',
        subtitle: 'Open the verification link we sent to continue.'
      }
    case LoginStep.CompleteSignUp:
      return {
        title: 'Create your password',
        subtitle: 'Choose a password you can use the next time you sign in.'
      }
    case LoginStep.ForgotPassword:
      return {
        title: 'Reset password',
        subtitle: 'We’ll send reset instructions to your email.'
      }
    case LoginStep.PasswordResetEmailSent:
      return {
        title: 'Check your email',
        subtitle: 'Your reset link is on the way.'
      }
    case LoginStep.PasswordResetCodeVerified:
      return {
        title: 'Create a new password',
        subtitle: 'Choose a strong password to secure your dashboard.'
      }
    case LoginStep.Login:
    default:
      return {
        title: 'Sign into Radom',
        subtitle: undefined
      }
    }
  })()

  const handleGoogleLoginError = (err: any): void => {
    setIsGoogleLoading(false)
    console.error('errored', err)
  }

  const login = useGoogleLogin((IS_LOCAL
    ? {
      flow: 'implicit',
      redirect_uri: GOOGLE_OAUTH_REDIRECT_URI ?? window.location.origin,
      onSuccess: async (tokenResponse: any) => {
        try {
          await Radom.loginWithSSO('google', tokenResponse.access_token, 'accessToken')
        } finally {
          setIsGoogleLoading(false)
        }
      },
      onError: handleGoogleLoginError,
      onNonOAuthError: handleGoogleLoginError
    }
    : {
      flow: 'auth-code',
      redirect_uri: GOOGLE_OAUTH_REDIRECT_URI ?? window.location.origin,
      onSuccess: async (tokenResponse: any) => {
        try {
          await Radom.loginWithSSO('google', tokenResponse.code, 'authorizationCode')
        } finally {
          setIsGoogleLoading(false)
        }
      },
      onError: handleGoogleLoginError,
      onNonOAuthError: handleGoogleLoginError
    }) as any)

  const verifyOnetimePassword = async (): Promise<void> => {
    if (!token) {
      return errorToast('Enter a valid token')
    }

    try {
      setIsVerifyingToken(true)
      const res = await Radom.verifyOneTimePassword(token)
      Radom.token = res.jwtToken
      localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, res.jwtToken)
      localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
      successToast('Successfully logged in')
      Radom.isLoggedIn = true
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsVerifyingToken(false)
    }
  }

  const [token, setToken] = useState<string>()
  const [isVerifyingToken, setIsVerifyingToken] = useState(false)

  if (Radom.isLoggedIn) {
    return props.children
  }

  return <Container>
    <LoginContainer>
      <LoginShell>
        <LoginTopBar>
          <RadomLogo style={{
            width: 98,
            height: 'auto'
          }} />
          <LoginTopBarAction>
            {!Radom.requires2fa && (
              <SecondaryButton
                type='button'
                onClick={() => setLoginStep(topBarActionStep)}
                style={{ fontSize: 13.75, minHeight: 42, padding: '0 18px', borderRadius: 12, fontWeight: 455 }}>
                {topBarActionLabel}
              </SecondaryButton>
            )}
          </LoginTopBarAction>
        </LoginTopBar>

        {!Radom.requires2fa && (
          <LoginStage>
            <LoginContent>
              <LoginIntro>
                <LoginTitle>{loginIntro.title}</LoginTitle>
                {loginIntro.subtitle && <LoginSubtitle>{loginIntro.subtitle}</LoginSubtitle>}
              </LoginIntro>

              {isPrimaryAuthStep
                ? (
                  <LoginCard style={{ minHeight: authCardMinHeight }}>
                    <PrimaryAuthSlot>
                      <SocialAuthStack>
                        <ProviderButton
                          type='button'
                          isLoading={isGoogleLoading}
                          onClick={() => {
                            setIsGoogleLoading(true)
                            login()
                          }}>
                          <img src={GoogleLogo as string} style={{ height: 18, width: 'auto' }} />
                          <span>{googleButtonLabel}</span>
                        </ProviderButton>

                        {showWalletLogin && (
                          <ProviderButton
                            type='button'
                            isLoading={User.isLoading}
                            onClick={async () => {
                              User.setupWalletConnection()
                              await User.connect()
                              if (User.isConnected) {
                                await Radom.login()
                              }
                            }}>
                            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                              <img src={Metamask as string} style={{ width: 'auto', height: 16 }} />
                              <img src={Brave} style={{ width: 'auto', height: 16 }} />
                            </div>
                            <span>Connect wallet</span>
                          </ProviderButton>
                        )}
                      </SocialAuthStack>

                      <DividerRow>
                        <DividerLine />
                        <DividerLabel>Or</DividerLabel>
                        <DividerLine />
                      </DividerRow>
                    </PrimaryAuthSlot>

                    <EmailLogin step={loginStep} setStep={(step) => { setLoginStep(step) }} />

                    {footerAgreementCopy && (
                      <CardFooterNote>
                        {footerAgreementCopy} you agree to the <FooterLink href="https://www.radom.com/terms" target='_blank'>Terms & Conditions</FooterLink> and <FooterLink href="https://www.radom.com/privacy" target='_blank'>Privacy Policy</FooterLink>.
                      </CardFooterNote>
                    )}
                  </LoginCard>
                )
                : (
                  <LoginCard style={{ minHeight: authCardMinHeight }}>
                    <PrimaryAuthSlotSpacer />
                    <EmailLogin step={loginStep} setStep={(step) => { setLoginStep(step) }} />
                  </LoginCard>
                )}
            </LoginContent>
          </LoginStage>
        )}

        {Radom.requires2fa && (
          <LoginStage>
            <LoginContent>
              <LoginIntro>
                <LoginTitle>Two-factor authentication</LoginTitle>
                <LoginSubtitle>
                  Open the verification app on your device and enter the current code to finish signing in.
                </LoginSubtitle>
              </LoginIntro>

              <LoginCard $compact style={{ minHeight: 280 }}>
                <form
                  onSubmit={e => {
                    e.preventDefault()
                    void verifyOnetimePassword()
                  }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <TextInput
                    onSubmit={verifyOnetimePassword}
                    autoFocus
                    value={token}
                    onChange={e => setToken(e.target.value)}
                    placeholder='Authentication code'
                    style={{ minHeight: 44, borderRadius: 12, padding: '9px 12px', fontSize: 14, fontWeight: 400 }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <PrimaryButton
                      type='submit'
                      isLoading={isVerifyingToken}
                      style={{ minHeight: 42, borderRadius: 12, fontSize: 13.75, fontWeight: 465 }}>
                      <span>Authenticate</span>
                    </PrimaryButton>

                    <SecondaryButton
                      type='button'
                      onClick={() => {
                        localStorage.clear()
                        window.location = '/' as any
                      }}
                      style={{ minHeight: 40, borderRadius: 12, fontSize: 13.75, fontWeight: 455 }}>
                      Back to login
                    </SecondaryButton>
                  </div>
                </form>
              </LoginCard>
            </LoginContent>
          </LoginStage>
        )}
      </LoginShell>
    </LoginContainer>

    <VidContainer style={{ pointerEvents: 'none', userSelect: 'none' }}>
      <Vid autoPlay muted loop>
        <source src={LoginVideo2} type="video/mp4" />
      </Vid>
    </VidContainer>
  </Container>
})

export default WithConnectedWallet
